<template>
  <div id="home">
    <b-container>
      <div class="title">
        <h1>
          {{ $t('dashboard.welcome', user()) }}
          <template v-if="user().email">
            ({{ user().email }})
          </template>
        </h1>
      </div>
      <template v-if="!isLoading">
        <b-row>
          <template v-if="$helper.isLegacy">
            <template v-for="onGoingTest in onGoingTests">
              <b-col
                v-if="onGoingTest.count > 0"
                :key="onGoingTest.kind"
                cols="12"
                class="mb-3"
              >
                <b-card>
                  <b-row>
                    <b-col class="d-flex align-items-center">
                      <strong>{{ $t(`legacy.dashboard.${onGoingTest.kind}.on_going.title`) }}</strong>
                    </b-col>
                    <b-col md="3">
                      <b-button
                        :to="{ name: onGoingTest.route_name }"
                        variant="primary"
                        class="text-uppercase"
                        block
                      >
                        {{ $t('actions.see_on_going_test') }}
                      </b-button>
                    </b-col>
                  </b-row>
                </b-card>
              </b-col>
            </template>
          </template>
          <b-col
            v-for="(card, idx) in cards"
            :key="idx"
            md="6"
          >
            <dashboard-card
              :title="card.title"
              :link="card.link"
              :numbers="card.numbers"
            />
          </b-col>
        </b-row>
        <b-card v-if="$helper.isLegacy && $helper.hasRolePcie && $helper.baseUrlVerifPcie" class="text-left mb-3">
          {{ $t('dashboard.verif_pcie') }}
          <a :href="verifLink" target="_blank">
            {{ verifLink }}
          </a>
        </b-card>
        <rgpd-card />
      </template>
      <loader-text
        v-else
        title="dashboard.loading"
      />
    </b-container>
    <b-modal
      v-if="$helper.isLegacy"
      id="modalPopup"
      v-model="user().popup"
      :title="$t('actions.create_login')"
      hide-footer
      @hide="skipStep"
    >
      <change-password :model-password="changePasswordFormModel" with-optional-fields />
      <b-form-checkbox
        id="checkboxSkipStep"
        v-model="checkedShowPopup"
      >
        {{ $t('actions.dont_show_popup') }}
      </b-form-checkbox>
    </b-modal>
  </div>
</template>

<script>
  import LoadOnNavigate from '@/mixins/load-on-navigate';
  import DashboardCard from '@/components/dashboard-card';
  import LoaderText from '@/components/loader-text';
  import RgpdCard from '@/components/rgpd-card';
  import ChangePassword from '@/components/change-password';

  export default {
    name: 'DashboardView',
    components: {
      DashboardCard,
      LoaderText,
      RgpdCard,
      ChangePassword,
    },
    mixins: [
      LoadOnNavigate,
    ],
    data() {
      return {
        checkedShowPopup: false,
        isLoading: true,
        careerNumber: null,
        examFailed: null,
        examNumber: null,
        examSucceed: null,
        onGoingTests: [],
        cards: [],
        changePasswordFormModel: {
          email: this.user().email || '',
          old_password: '',
          password: '',
          password_confirm: '',
        },
      };
    },
    computed: {
      verifLink() {
        return `${this.$helper.baseUrlVerifPcie}/${this.user().id}`;
      },
    },
    methods: {
      async skipStep() {
        if (this.checkedShowPopup) {
          await this.$helper.popupHide();
        }
      },
      async load() {
        this.isLoading = true;
        const data = await this.$ajax.get('dashboard/');
        if (this.$helper.isLegacy) {
          this.onGoingTests = [{
            kind: 'eval',
            route_name: 'evaluation',
            count: data.ongoing_eval_count,
          }, {
            kind: 'certif',
            route_name: 'certification',
            count: data.ongoing_certif_count,
          }, {
            kind: 'prepa',
            route_name: 'preparation',
            count: data.ongoing_prepa_count,
          }];

          if (this.$helper.hasRolePcie) {
            this.cards = [{
              title: this.$t('legacy.dashboard.eval.pending.title'),
              link: { name: 'evaluation' },
              numbers: [{
                number: data.pending_eval_count,
                title: this.$t('legacy.dashboard.eval.pending.count'),
              }],
            }, {
              title: this.$t('legacy.dashboard.certif.pending.title'),
              link: { name: 'certification' },
              numbers: [{
                number: data.pending_certif_count,
                title: this.$t('legacy.dashboard.certif.pending.count'),
              }],
            }, {
              title: this.$t('legacy.dashboard.eval.done.title'),
              link: { name: 'completed_test' },
              numbers: [{
                number: data.done_eval_count,
                title: this.$t('legacy.dashboard.eval.done.total'),
              }],
            }, {
              title: this.$t('legacy.dashboard.certif.done.title'),
              link: { name: 'completed_test' },
              numbers: [{
                number: data.done_certif_count,
                title: this.$t('legacy.dashboard.certif.done.total'),
              }, {
                number: data.done_succeeded_certif_count,
                title: this.$t('legacy.dashboard.certif.done.succeeded'),
                class: 'text-success',
              }, {
                number: data.done_failed_certif_count,
                title: this.$t('legacy.dashboard.certif.done.failed'),
                class: 'text-danger',
              }],
            }];
          } else {
            this.cards = [{
              title: this.$t('legacy.dashboard.eval.pending.title'),
              link: { name: 'evaluation' },
              numbers: [{
                number: data.pending_eval_count,
                title: this.$t('legacy.dashboard.eval.pending.count'),
              }],
            }, {
              title: this.$t('legacy.dashboard.eval.done.title'),
              link: { name: 'completed_test' },
              numbers: [{
                number: data.done_eval_count,
                title: this.$t('legacy.dashboard.eval.done.total'),
              }],
            }];
          }
        } else {
          const $data = this._.mapKeys(
            this._.pick(data, 'pending_eval_count', 'done_failed_eval_count', 'done_eval_count', 'done_succeeded_eval_count'),
            (value, key) => this._.camelCase(key),
          );
          this._.merge(this.$data, $data);

          this.cards.push({
            title: this.$t('dashboard.tests'),
            link: { name: 'evaluation' },
            numbers: [{
              number: this.$data.pendingEvalCount,
              title: this.$t('dashboard.total'),
            }],
          });

          const number = [{
            number: this.$data.doneEvalCount,
            title: this.$t('dashboard.total'),
          }];

          if (this.$helper.showValidatedResult) {
            number.push({
              number: this.$data.doneSucceededEvalCount,
              title: this.$t('dashboard.exam_succeed'),
              class: 'text-success',
            });

            number.push({
              number: this.$data.doneFailedEvalCount,
              title: this.$t('dashboard.exam_failed'),
              class: 'text-danger',
            });
          }

          this.cards.push({
            title: this.$t('dashboard.exam'),
            link: { name: 'completed_test' },
            numbers: number,
          });
        }

        this.isLoading = false;
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import '~@/scss/vars';

  .title {
    background-color: $primary;
    padding: 1rem;
    margin-bottom: 2rem;

    h1 {
      margin: 0;
      font-size: 1.2rem;
      color: $white;
    }
  }
</style>
